/**
 * Shared list sort contract for Ant Tables + list APIs.
 *
 * Query params:
 *   sort_by: checkin_date | checkout_date | items | requested | approved | amount
 *   sort_dir: asc | desc
 *
 * Sorting is always server-side (ORDER BY + pagination). Column `sorter: true`
 * must not use a client comparator.
 */

export const SORT_BY = {
  CHECKIN_DATE: "checkin_date",
  CHECKOUT_DATE: "checkout_date",
  ITEMS: "items",
  REQUESTED: "requested",
  APPROVED: "approved",
  AMOUNT: "amount",
  TAT: "tat",
  // Facilities within a claims month, by their latest claim SUBMISSION (spec §9).
  LATEST_SUBMISSION: "latest_submission",
};

export const SORT_DIR = {
  ASC: "asc",
  DESC: "desc",
};

const KEY_ALIASES = {
  requested_cost: SORT_BY.REQUESTED,
  total_requested_cost: SORT_BY.REQUESTED,
  approved_cost: SORT_BY.APPROVED,
  total_approved_cost: SORT_BY.APPROVED,
  total_cost: SORT_BY.AMOUNT,
  encounter_cost: SORT_BY.AMOUNT,
  pa_items_count: SORT_BY.ITEMS,
  line_items: SORT_BY.ITEMS,
  items_count: SORT_BY.ITEMS,
  total_items: SORT_BY.ITEMS,
  encounter_date: SORT_BY.CHECKIN_DATE,
  claims_date: SORT_BY.CHECKIN_DATE,
  last_claim_date: SORT_BY.CHECKIN_DATE,
  last_encounter_date: SORT_BY.CHECKIN_DATE,
  tat_seconds: SORT_BY.TAT,
  turnaround: SORT_BY.TAT,
  turnaround_time: SORT_BY.TAT,
};

export function canonicalizeSortBy(value) {
  const key = String(value || "").trim().toLowerCase();
  if (!key) return "";
  return KEY_ALIASES[key] || key;
}

export function parseSortParams(source, defaults = { sort_by: SORT_BY.CHECKIN_DATE, sort_dir: SORT_DIR.DESC }) {
  const get = (k) => {
    if (!source) return null;
    if (typeof source.get === "function") return source.get(k);
    return source[k];
  };
  const rawBy = get("sort_by") || get("sort") || get("sortBy") || defaults.sort_by;
  const rawDir = get("sort_dir") || get("sort_order") || get("sortOrder") || defaults.sort_dir;
  const sort_by = canonicalizeSortBy(rawBy) || defaults.sort_by;
  const dir = String(rawDir || defaults.sort_dir).toLowerCase();
  const sort_dir = dir === SORT_DIR.ASC ? SORT_DIR.ASC : SORT_DIR.DESC;
  return { sort_by, sort_dir };
}

/** Map Ant Design table `sorter` → API query params. */
export function sorterToQuery(sorter, fieldToSortBy = {}) {
  const field = sorter?.columnKey || sorter?.field;
  if (!field || !sorter?.order) {
    return { sort_by: undefined, sort_dir: undefined };
  }
  const mapped = fieldToSortBy[field] || field;
  return {
    sort_by: canonicalizeSortBy(mapped) || mapped,
    sort_dir: sorter.order === "ascend" ? SORT_DIR.ASC : SORT_DIR.DESC,
  };
}

export function sortOrderForColumn(sortBy, sortDir, columnKey) {
  if (!sortBy || sortBy !== columnKey) return null;
  return sortDir === SORT_DIR.ASC ? "ascend" : "descend";
}

/** Attach server-side Ant sorter metadata to a column definition. */
export function withServerSorter(column, sortKey, current = {}) {
  return {
    ...column,
    key: column.key || sortKey,
    sorter: true,
    sortOrder: sortOrderForColumn(current.sort_by, current.sort_dir, sortKey),
  };
}

export function toSortQuery(sortBy, sortDir) {
  if (!sortBy) return {};
  return {
    sort_by: sortBy,
    sort_dir: sortDir === SORT_DIR.ASC ? SORT_DIR.ASC : SORT_DIR.DESC,
  };
}

export function sortSelectValue(sortBy, sortDir) {
  return `${sortBy || SORT_BY.CHECKIN_DATE}:${sortDir || SORT_DIR.DESC}`;
}

export function parseSortSelectValue(value, fallback = { sort_by: SORT_BY.CHECKIN_DATE, sort_dir: SORT_DIR.DESC }) {
  const raw = String(value || "");
  const idx = raw.indexOf(":");
  if (idx < 0) return fallback;
  const sort_by = canonicalizeSortBy(raw.slice(0, idx)) || fallback.sort_by;
  const dir = raw.slice(idx + 1).toLowerCase();
  return {
    sort_by,
    sort_dir: dir === SORT_DIR.ASC ? SORT_DIR.ASC : SORT_DIR.DESC,
  };
}

export const DATE_AMOUNT_ITEM_SORT_OPTIONS = [
  { value: "checkin_date:desc", label: "Date · newest" },
  { value: "checkin_date:asc", label: "Date · oldest" },
  { value: "amount:desc", label: "Amount · high–low" },
  { value: "amount:asc", label: "Amount · low–high" },
  { value: "items:desc", label: "Items · high–low" },
  { value: "items:asc", label: "Items · low–high" },
];

export const TAT_SORT_OPTIONS = [
  { value: "tat:desc", label: "TAT · longest" },
  { value: "tat:asc", label: "TAT · shortest" },
];

/** Spec §9 — facility lists inside a claims month (new flow). */
export const LATEST_SUBMISSION_SORT_OPTIONS = [
  { value: "latest_submission:desc", label: "Latest submission · newest" },
  { value: "latest_submission:asc", label: "Latest submission · oldest" },
];
